import { useState, useCallback } from 'react'
import {
  LayoutGrid, Building2, Users as UsersIcon, CreditCard, Inbox, Mail,
  Database, SlidersHorizontal, ArrowUpRight, LogOut, MoreHorizontal, ChevronRight,
} from 'lucide-react'
import { useOrgData } from '../hooks/useOrgData'
import { useBranchUsage } from '../hooks/useBranchUsage'
import { useUsers } from '../hooks/useUsers'
import { useDailyData } from '../hooks/useTankData'
import { useSupport } from '../hooks/useSupport'
import { useBroadcasts, usePaystackEvents } from '../hooks/useBroadcasts'
import { useAuditLog } from '../hooks/useAuditLog'
import UsersPanel from '../components/users/UsersPanel'
import ManagePlanModal from '../components/organizations/ManagePlanModal'
import Toast from '../components/common/Toast'
import Overview from './views/Overview'
import Organizations from './views/Organizations'
import Billing from './views/Billing'
import DemoData from './views/DemoData'
import SettingsView from './views/SettingsView'
import InboxView from './views/Inbox'
import EmailView from './views/Email'

const NAV = [
  { group: null, items: [{ key: 'overview', label: 'Overview', icon: LayoutGrid }] },
  {
    group: 'Customers',
    items: [
      { key: 'orgs', label: 'Organizations', icon: Building2 },
      { key: 'users', label: 'Users', icon: UsersIcon },
      { key: 'billing', label: 'Billing', icon: CreditCard },
    ],
  },
  {
    group: 'Support',
    items: [
      { key: 'inbox', label: 'Inbox', icon: Inbox },
      { key: 'email', label: 'Email', icon: Mail },
    ],
  },
  {
    group: 'Platform',
    items: [
      { key: 'demo', label: 'Demo data', icon: Database },
      { key: 'settings', label: 'Settings', icon: SlidersHorizontal },
    ],
  },
]

const TITLES = {
  overview: 'Overview', orgs: 'Organizations', users: 'Users', billing: 'Billing',
  inbox: 'Support inbox', email: 'Email broadcasts', demo: 'Demo data', settings: 'Settings', more: 'More',
}

const MOBILE_TABS = ['overview', 'orgs', 'users', 'inbox']

function initials(name = '') {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
}

export default function AdminShell({ currentUser, onLogout, navigate }) {
  const {
    organizations, branches, addOrganization, updateOrganization, deleteOrganization,
    applyPlanAndTrial, suspendOrganization, restoreOrganization,
  } = useOrgData()
  const usage = useBranchUsage()
  const {
    users, addUser, assignUser, setUserRole, deactivateUser, reactivateUser,
    resetPassword, deleteUser, setUsersActive, resetPasswords, refetchUsers,
  } = useUsers(currentUser)
  const { importEntry, refetch: refetchEntries } = useDailyData()
  const support = useSupport(currentUser)
  const broadcasts = useBroadcasts(currentUser)
  const { events: paystackEvents } = usePaystackEvents(currentUser)
  const audit = useAuditLog(currentUser)

  const [view, setView] = useState('overview')
  const [managingOrg, setManagingOrg] = useState(null)
  const [toast, setToast] = useState(null)
  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), [])

  async function handleManageSave(orgId, changes) {
    const result = await applyPlanAndTrial(orgId, changes)
    if (result?.success !== false) {
      showToast(`Plan & trial updated for "${managingOrg.name}".`)
      audit.log('plan_trial_update', managingOrg.name, changes)
    }
    return result
  }

  // ── Audited wrappers: every mutating HQ action lands in admin_audit_log ────
  const userName = (id) => users.find((u) => u.id === id)?.name || id
  const audited = (fn, action, label) => async (...args) => {
    const result = await fn(...args)
    if (result?.success !== false) audit.log(action, label(...args))
    return result
  }
  const orgActions = {
    onAdd: audited(addOrganization, 'org_create', (orgData) => orgData.name),
    onUpdate: audited(updateOrganization, 'org_update', (orgId, orgData) => orgData.name),
    onDelete: audited(deleteOrganization, 'org_delete',
      (orgId) => organizations.find((o) => o.id === orgId)?.name || orgId),
    onSuspend: audited(suspendOrganization, 'org_suspend',
      (orgId, reason) => `${organizations.find((o) => o.id === orgId)?.name || orgId} — ${reason || 'no reason given'}`),
    onRestore: audited(restoreOrganization, 'org_restore',
      (orgId) => organizations.find((o) => o.id === orgId)?.name || orgId),
  }
  const userActions = {
    onAddUser: audited(addUser, 'user_create', (u) => u.name),
    onAssignUser: audited(assignUser, 'user_assign', userName),
    onSetUserRole: audited(setUserRole, 'user_role_change', (id, role) => `${userName(id)} → ${role}`),
    onDeactivateUser: audited(deactivateUser, 'user_deactivate', userName),
    onReactivateUser: audited(reactivateUser, 'user_reactivate', userName),
    onResetPassword: audited(resetPassword, 'user_password_reset', userName),
    onDeleteUser: audited(deleteUser, 'user_delete', userName),
    onSetUsersActive: audited(setUsersActive, 'user_bulk_active',
      (ids, isActive) => `${ids.length} user${ids.length !== 1 ? 's' : ''} → ${isActive ? 'active' : 'inactive'}`),
    onResetPasswords: audited(resetPasswords, 'user_bulk_password_reset',
      (ids) => `${ids.length} user${ids.length !== 1 ? 's' : ''}`),
    onRefetchUsers: refetchUsers,
  }
  const auditedBroadcasts = {
    ...broadcasts,
    sendBroadcast: async (payload) => {
      const result = await broadcasts.sendBroadcast(payload)
      if (result?.success && !payload.testTo) {
        audit.log('broadcast_send', payload.segment, { subject: payload.subject, sent: result.sent })
      }
      return result
    },
  }

  const openCustomerApp = () => navigate('/dashboard')

  function NavItem({ item, mobile = false }) {
    const Icon = item.icon
    const active = view === item.key
    const unread = item.key === 'inbox' ? support.unreadCount : 0
    if (mobile) {
      return (
        <button onClick={() => setView(item.key)}
          className={`relative flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10.5px] font-semibold rounded-lg transition ${
            active ? 'text-teal-500' : 'text-gray-400'
          }`}>
          <Icon className="w-[18px] h-[18px]" />
          {item.label}
          {unread > 0 && (
            <span className="absolute top-0.5 right-[22%] min-w-[16px] h-4 px-1 rounded-full bg-teal-500 text-white text-[9.5px] font-bold grid place-items-center">
              {unread}
            </span>
          )}
        </button>
      )
    }
    return (
      <button onClick={() => setView(item.key)}
        className={`w-full flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg text-[13.5px] transition ${
          active
            ? 'bg-teal-400/10 text-teal-300 font-semibold'
            : 'text-[#7E938F] hover:text-[#D7E2DF] hover:bg-white/5 font-medium'
        }`}>
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{item.label}</span>
        {unread > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-teal-400 text-[#06231D] text-[10.5px] font-bold grid place-items-center">
            {unread}
          </span>
        )}
        {item.soon && (
          <span className="text-[9px] font-extrabold tracking-wide text-[#0A1413] bg-amber-400 rounded px-1 py-0.5">V2</span>
        )}
      </button>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* ── Sidebar (desktop) ── */}
      <aside className="hidden md:flex flex-col w-[232px] flex-none sticky top-0 h-screen bg-[#0E1817] border-r border-[#1D2C2A]">
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-[#1D2C2A]">
          <div className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-teal-400 to-teal-700 grid place-items-center text-[#06231D] text-[13px] font-extrabold">
            PT
          </div>
          <span className="text-white font-semibold text-[14.5px]">PetroTrack</span>
          <span className="text-[10px] font-extrabold tracking-wider text-teal-300 bg-teal-400/10 border border-teal-400/30 rounded px-1.5 py-0.5">HQ</span>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4">
          {NAV.map((g, i) => (
            <div key={i}>
              {g.group && (
                <p className="text-[10.5px] font-bold uppercase tracking-widest text-[#7E938F] px-2.5 mb-1.5">{g.group}</p>
              )}
              <div className="space-y-0.5">
                {g.items.map((item) => <NavItem key={item.key} item={item} />)}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-[#1D2C2A] px-3.5 py-3 space-y-2.5">
          <button onClick={openCustomerApp}
            className="flex items-center gap-1.5 text-xs text-[#7E938F] hover:text-[#D7E2DF] transition">
            <ArrowUpRight className="w-3.5 h-3.5" /> Open customer app
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-[30px] h-[30px] rounded-full bg-[#1E3B36] text-teal-300 grid place-items-center text-[11px] font-bold flex-none">
              {initials(currentUser.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-[#D7E2DF] truncate">{currentUser.name}</p>
              <p className="text-[11px] text-[#7E938F]">Super Admin</p>
            </div>
            <button onClick={onLogout} title="Sign out"
              className="p-1.5 text-[#7E938F] hover:text-red-400 rounded-lg transition">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-2.5 px-4 md:px-6 py-3 bg-gray-50/95 backdrop-blur border-b border-gray-200">
          <div className="md:hidden w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400 to-teal-700 grid place-items-center text-[#06231D] text-[11px] font-extrabold">
            PT
          </div>
          <span className="hidden md:inline text-[13px] text-gray-400">HQ /</span>
          <h1 className="text-[15px] font-bold text-gray-800">{TITLES[view]}</h1>
        </header>

        <main className="flex-1 px-4 md:px-6 py-5 pb-28 max-w-6xl w-full mx-auto">
          {view === 'overview' && (
            <Overview organizations={organizations} branches={branches} users={users}
              auditEntries={audit.entries} onManage={setManagingOrg} onGoto={setView} />
          )}
          {view === 'orgs' && (
            <Organizations organizations={organizations} branches={branches} users={users}
              onManage={setManagingOrg} {...orgActions} showToast={showToast} usage={usage} />
          )}
          {view === 'users' && (
            <UsersPanel currentUser={currentUser} users={users} organizations={organizations} branches={branches}
              enableTable onManageOrg={setManagingOrg} {...userActions} />
          )}
          {view === 'billing' && <Billing organizations={organizations} events={paystackEvents} users={users} />}
          {view === 'inbox' && <InboxView support={support} organizations={organizations} />}
          {view === 'email' && (
            <EmailView organizations={organizations} users={users} currentUser={currentUser}
              broadcasts={auditedBroadcasts} showToast={showToast} />
          )}
          {view === 'demo' && (
            <DemoData branches={branches}
              onAddEntry={(tankId, entry) => importEntry(entry)} onImported={refetchEntries} />
          )}
          {view === 'settings' && <SettingsView showToast={showToast} onAudit={audit.log} />}
          {view === 'more' && (
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              {[
                { key: 'billing', label: 'Billing', icon: CreditCard },
                { key: 'email', label: 'Email broadcasts', icon: Mail },
                { key: 'demo', label: 'Demo data', icon: Database },
                { key: 'settings', label: 'Settings', icon: SlidersHorizontal },
              ].map(({ key, label, icon: Icon, soon }) => (
                <button key={key} onClick={() => setView(key)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 border-b border-gray-100 transition">
                  <Icon className="w-5 h-5 text-teal-600" />
                  <span className="flex-1 text-left text-sm font-semibold text-gray-800">{label}</span>
                  {soon && <span className="text-[9px] font-extrabold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">V2</span>}
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </button>
              ))}
              <button onClick={openCustomerApp}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 border-b border-gray-100 transition">
                <ArrowUpRight className="w-5 h-5 text-teal-600" />
                <span className="flex-1 text-left text-sm font-semibold text-gray-800">Open customer app</span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
              <button onClick={onLogout}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-red-50 transition">
                <LogOut className="w-5 h-5 text-red-500" />
                <span className="flex-1 text-left text-sm font-semibold text-red-500">Sign out</span>
              </button>
            </div>
          )}
        </main>
      </div>

      {/* ── Bottom nav (mobile) ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex bg-white border-t border-gray-200 px-1 pt-1"
        style={{ paddingBottom: 'max(4px, env(safe-area-inset-bottom))' }}>
        {NAV.flatMap((g) => g.items).filter((i) => MOBILE_TABS.includes(i.key)).map((item) => (
          <NavItem key={item.key} item={item} mobile />
        ))}
        <NavItem mobile item={{ key: 'more', label: 'More', icon: MoreHorizontal }} />
      </nav>

      {managingOrg && (
        <ManagePlanModal
          org={managingOrg}
          branchCount={branches.filter((b) => b.organizationId === managingOrg.id).length}
          onSave={handleManageSave}
          onClose={() => setManagingOrg(null)}
        />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  )
}
